import { useNavigate } from 'react-router-dom';
import { Loader2, MapPin, ShieldAlert, X } from 'lucide-react';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { usePanicSos, PANIC_HOLD_MS } from '@features/panic/usePanicSos';
import { WidgetCard } from './WidgetCard';
import { widgetTitle, type WidgetProps } from './registry';

/**
 * Home widget: a compact, working version of the Panic/SOS screen — shows any
 * active alert, the member's current coordinates, and a horizontally
 * scrollable rail of hold-to-alert emergency tiles. "Open" jumps to the full
 * panic page (history, contacts, map).
 */
export function PanicWidget({ config }: WidgetProps): JSX.Element {
  const navigate = useNavigate();
  const panic = usePanicSos();

  return (
    <>
    <WidgetCard title={widgetTitle(config)} actionLabel="Open" onAction={() => navigate('/app/panic')} onClick={() => navigate('/app/panic')}>
      {/* Active alert */}
      {panic.activeLog ? (
        <div
          className="mb-3 flex items-center gap-3 rounded-xl px-3 py-2.5"
          style={{ backgroundColor: 'color-mix(in oklab, var(--danger) 14%, var(--surface-card))' }}
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-pill" style={{ backgroundColor: 'var(--danger)' }}>
            <ShieldAlert className="h-4.5 w-4.5 text-white" strokeWidth={2} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-body-sm font-semibold text-ink">Alert active</p>
            <p className="truncate text-caption text-ink-secondary">
              {statusLabel(panic.activeLog.status)} · estate security notified
            </p>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              panic.cancelActive(panic.activeLog!.panic_log_id);
            }}
            disabled={panic.cancelling}
            className="flex shrink-0 items-center gap-1 rounded-pill bg-card px-2.5 py-1 text-caption font-semibold text-danger hairline disabled:opacity-60"
          >
            <X className="h-3.5 w-3.5" strokeWidth={2} />
            Cancel
          </button>
        </div>
      ) : null}

      {/* Current coordinates */}
      <div className="mb-3 flex items-center gap-1.5 text-caption text-ink-secondary">
        <MapPin className="h-3.5 w-3.5 shrink-0 text-ink-muted" strokeWidth={1.75} />
        {panic.coords ? (
          <span className="tabular">
            {panic.coords.latitude.toFixed(5)}, {panic.coords.longitude.toFixed(5)}
            {panic.coords.accuracy ? ` · ±${Math.round(panic.coords.accuracy)}m` : ''}
          </span>
        ) : panic.locating ? (
          <span className="flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} /> Locating…
          </span>
        ) : (
          <span>Location unavailable</span>
        )}
      </div>

      {/* Hold-to-alert tiles — horizontally scrollable */}
      {panic.isLoading ? (
        <div className="flex gap-2">
          <Skeleton className="h-24 w-28 shrink-0 rounded-2xl" />
          <Skeleton className="h-24 w-28 shrink-0 rounded-2xl" />
        </div>
      ) : panic.tiles.length === 0 ? (
        <p className="rounded-lg bg-sunken px-4 py-4 text-center text-caption text-ink-secondary">
          No emergency types configured.
        </p>
      ) : (
        <div className="-mx-4 flex gap-2.5 overflow-x-auto px-4 pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {panic.tiles.map((tile) => {
            const holding = panic.holdingId === tile.id;
            const Icon = tile.icon;
            return (
              <button
                key={tile.id}
                type="button"
                disabled={panic.sending}
                style={{ backgroundColor: tile.color, touchAction: 'pan-x pan-y' }}
                onPointerDown={(e) => panic.startHold(e, tile, tile.id)}
                onClick={(e) => e.stopPropagation()}
                onContextMenu={(e) => e.preventDefault()}
                className={
                  'relative flex h-24 w-28 shrink-0 select-none flex-col overflow-hidden rounded-2xl p-3 text-left text-white transition-transform duration-fast ' +
                  (holding ? 'scale-[0.98]' : '')
                }
              >
                <span
                  aria-hidden
                  className="absolute inset-x-0 bottom-0 origin-bottom bg-white/20"
                  style={{
                    height: '100%',
                    transform: holding ? 'scaleY(1)' : 'scaleY(0)',
                    transition: holding ? `transform ${PANIC_HOLD_MS}ms linear` : 'transform 150ms ease',
                  }}
                />
                <span className="relative flex h-7 w-7 items-center justify-center rounded-pill bg-white/20">
                  <Icon className="h-4 w-4" strokeWidth={1.75} />
                </span>
                <span className="relative mt-auto block">
                  <span className="block truncate text-body-sm font-semibold leading-tight">{tile.title}</span>
                  <span className="mt-0.5 block whitespace-nowrap text-[0.625rem] font-semibold uppercase leading-tight text-white/85">
                    {holding ? 'Keep holding…' : 'Hold to alert'}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </WidgetCard>

    {/* Full-screen sending mask — mirrors the panic page. Solid branded
        backdrop (the `primary-*` palette has no <alpha-value> channel, so an
        /opacity modifier would render transparent); inline rgba fallback keeps
        it opaque before branding vars resolve. */}
    {panic.sending ? (
      <div
        className="fixed inset-0 z-[70] flex flex-col items-center justify-center gap-5 bg-primary-900 text-white"
        style={{ backgroundColor: 'var(--primary-900, #0f172a)' }}
        role="status"
        aria-live="assertive"
      >
        <span className="relative flex h-24 w-24 items-center justify-center">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-pill bg-white/30" />
          <span className="relative flex h-24 w-24 items-center justify-center rounded-pill bg-white/15">
            <ShieldAlert className="h-12 w-12 text-white" strokeWidth={2.2} />
          </span>
        </span>
        <div className="flex items-center gap-2.5">
          <Loader2 className="h-6 w-6 animate-spin text-white" />
          <span className="font-display text-display-lg">Sending…</span>
        </div>
        <span className="text-label text-white/80">Alerting your emergency contacts</span>
      </div>
    ) : null}
    </>
  );
}

function statusLabel(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
